import React, { useCallback, useState } from 'react';
import { useRightPanelWidth } from '../../app/layout/RightPanel';
import { useSidebarOpenState } from '../../app/layout/SidebarOpenStateContext';
import Caret from '../../components/Caret';
import PopOverPrimitive from '../../components/PopOverPrimitive';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import seriesIcon from '../../icons/non-prefilled/16x16/series.svg';
import SeriesHoverCard from '../../series/SeriesHoverCard';
import flipOnShow from '../../utils/popover/flipOnShow';
import resizeOnShow from '../../utils/popover/resizeOnShow';
import { SIDEBAR_WIDTH } from '../PostLayoutSizes';
import ControlBarButton from './ControlBarButton';
import * as styles from './ControlBarSeriesButton.module.scss';

const POPOVER_PADDING = 12;
const POPOVER_OFFSET = 4;

const SERIES_FRAGMENT = gql(`
  fragment ControlBarSeriesButton_series on SeriesFields {
    id
    name
    posts {
      id
    }
  }
`);

const SERIES_DETAILS_FRAGMENT = gql(`
  fragment ControlBarSeriesButton_seriesDetails on SeriesFields {
    id
    ...SeriesHoverCard_series
  }
`);

interface ControlBarSeriesButtonProps {
  postId: string;
  series: DocumentType<typeof SERIES_FRAGMENT>;
  seriesDetails?: DocumentType<typeof SERIES_DETAILS_FRAGMENT>;
}

const ControlBarSeriesButton = ({
  postId,
  series,
  seriesDetails,
}: ControlBarSeriesButtonProps) => {
  const index = series.posts.findIndex(({ id }) => id === postId);
  const [isOpen, setOpen] = useState(false);
  const { isSidebarOpen } = useSidebarOpenState();
  const rightPanelWidth = useRightPanelWidth();

  const getAdditionalMiddleware = useCallback<
    NonNullable<
      React.ComponentProps<typeof PopOverPrimitive>['getAdditionalMiddleware']
    >
  >((options) => {
    return [
      flipOnShow({
        fallbackPlacement: 'top-start',
        padding: options.padding,
      }),
      resizeOnShow({
        padding: options.padding,
      }),
    ];
  }, []);

  return (
    <PopOverPrimitive
      onOpenChange={setOpen}
      open={isOpen}
      screenPadding={{
        top: POPOVER_PADDING,
        bottom: POPOVER_PADDING,
        left: POPOVER_PADDING + (isSidebarOpen ? SIDEBAR_WIDTH : 0),
        right: POPOVER_PADDING + rightPanelWidth,
      }}
      offset={POPOVER_OFFSET}
      placement="bottom-start"
      trigger="click"
      autoUpdateAnimationFrame
      noFlip
      getAdditionalMiddleware={getAdditionalMiddleware}
    >
      <PopOverPrimitive.Trigger asChild>
        <div className={styles.container}>
          <ControlBarButton
            active={isOpen}
            className={styles.seriesButton}
            icon={<SVGIcon className={styles.icon}>{seriesIcon}</SVGIcon>}
          >
            <div className={styles.seriesButtonContent}>
              <div className={styles.seriesInfo}>
                Part {index + 1} of {series.posts.length} in{' '}
                <span className={styles.seriesName}>{series.name}</span>
              </div>
              <div className={styles.dropdownCaret}>
                <Caret direction={isOpen ? 'up' : 'down'} size={10} />
              </div>
            </div>
          </ControlBarButton>
        </div>
      </PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating
        className={styles.popOverFloatingContainer}
        noPortal
      >
        <PopOverPrimitive.Content className={styles.popOverContent}>
          <SeriesHoverCard
            series={seriesDetails}
            postId={postId}
            showEditButton
            onClickEditButton={() => setOpen(false)}
          />
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

export default ControlBarSeriesButton;
